import { Stack } from "@mui/material";
import { StackProps } from "@mui/material";
import { Typography, TypographyProps } from "@mui/material";

import { LABEL_FOR_PLAN } from "@app/analytics/account/Billing/constants";
import Link from "@app/common/components/Link";
import ProductFeatureList from "@app/common/components/ProductFeatureList";
import ProductFeatureListItem from "@app/common/components/ProductFeatureListItem";
import LINKS from "@app/common/links";
import ROUTES from "@app/common/routes";
import Config from "@app/config";

const TutorialContentWrapper = ({ children, sx, ...props }: StackProps) => {
  return (
    <Stack sx={{ ...sx }} spacing={2} {...props}>
      {children}
    </Stack>
  );
};

const TutorialContent = ({ children, sx, ...props }: TypographyProps) => {
  return (
    <Typography
      variant="bodyPrimaryReg"
      sx={{ color: "shades.neutral.700", ...sx }}
      {...props}
    >
      {children}
    </Typography>
  );
};

export const CMOStep = () => {
  return (
    <TutorialContentWrapper>
      <TutorialContent>
        You can count on me to provide real-time meeting measurement,
        personalized meeting analytics, and coaching.{" "}
        <span style={{ fontWeight: 500 }}>
          Watch the short video above to learn more!
        </span>
      </TutorialContent>
      <TutorialContent>
        Read Dashboard is your hub for all reports and insights, including
        meeting trends over time and recommendations. Continue to discover how
        Read can improve your meeting wellness.
      </TutorialContent>
    </TutorialContentWrapper>
  );
};

export const PlaybackStep = () => {
  return (
    <TutorialContentWrapper spacing={0}>
      <TutorialContent sx={{ pb: 2 }}>
        Get a comprehensive understanding of every meeting with{" "}
        <Link
          href={`${ROUTES.MEETING_REPORTS}?ht=true`}
          sx={{ color: "shades.primary.500", fontWeight: 500 }}
        >
          Read Meeting Reports
        </Link>
        . Understand what happened in the meeting, participant reactions, and
        how it could have been improved.
      </TutorialContent>
      <TutorialContent sx={{ pb: 1 }}>
        With recording enabled, get access to a full transcript and playback,
        plus detailed meeting metrics.
      </TutorialContent>
    </TutorialContentWrapper>
  );
};

const ZOOM_FREE_TRIAL_BULLETS = [
  {
    text: "Workspaces",
    url: LINKS.MARKETING.WORKSPACES,
  },
  {
    text: "Meeting playback + transcription",
    url: LINKS.MARKETING.PLAYBACK,
  },
  {
    text: "Advanced reporting",
    url: LINKS.MARKETING.DASHBOARD,
  },
  {
    text: "Participant-level metrics",
    url: LINKS.MARKETING.MEETING_TOOLS,
  },
];

const FREE_TRIAL_BULLETS = [
  ...ZOOM_FREE_TRIAL_BULLETS,
  {
    text: "Benchmarks",
    url: LINKS.MARKETING.BENCHMARKS,
  },
  {
    text: "Unlimited meetings",
    url: LINKS.MARKETING.PLANS_PRICING,
  },
];

export const FreeTrialStep = () => {
  return (
    <TutorialContentWrapper spacing={0}>
      <TutorialContent sx={{ pb: 1 }}>You now have access to:</TutorialContent>
      <ProductFeatureList>
        {FREE_TRIAL_BULLETS.map((bullet) => {
          return (
            <ProductFeatureListItem key={bullet.text} href={bullet.url}>
              {bullet.text}
            </ProductFeatureListItem>
          );
        })}
      </ProductFeatureList>
    </TutorialContentWrapper>
  );
};

export const FreeTrialStepZoom = () => {
  return (
    <TutorialContentWrapper spacing={0}>
      <TutorialContent
        sx={{ fontSize: "0.875rem", lineHeight: "1.125rem", pb: 1 }}
      >
        For the next {Config.billing.trialDays} days, you also have access to a{" "}
        <Link
          href={ROUTES.BILLING}
          sx={{ color: "shades.primary.500", fontWeight: 500 }}
        >
          Read Enterprise
        </Link>{" "}
        free trial! After {Config.billing.trialDays} days, you can upgrade or
        continue to get premium features included in{" "}
        {LABEL_FOR_PLAN.zoom_bundle_trial}.
      </TutorialContent>
      <TutorialContent sx={{ pb: 1 }}>You now have access to:</TutorialContent>
      <ProductFeatureList>
        {ZOOM_FREE_TRIAL_BULLETS.map((bullet) => {
          return (
            <ProductFeatureListItem key={bullet.text} href={bullet.url}>
              {bullet.text}
            </ProductFeatureListItem>
          );
        })}
      </ProductFeatureList>
    </TutorialContentWrapper>
  );
};
